// SlicePlugin definition for the textbox slice (PRD-F6 §TR11).
//
// T3 stub: capability descriptor list and `createDefaults` wired up. Renderer
// (`createNode` / `updateNode`) and edit-mode aspects are added in T5+.
//
// OUT_OF_SCOPE (PRD-F6 §"Out of Scope" — verbatim mirror, do not extend here):
//   - P6 Outer-API-Reduktion (Element ≤ 350 LOC — handled by P6, not this slice)
//   - Bezier / orthogonal line strategies (P5.5 Out-of-Scope — stays out)
//   - Stack-Panel (P6+)
//   - Migration of legacy board content (v0.x boards with old textbox structure)
//   - Konva-native text cursor / text selection in Konva renderer (= Variant B)
//   - Accessibility / ARIA annotations for Konva text (P6+)
//   - Table rendering (post-P6)
//   - New font levels / heading rendering (not MVP)
//   - Custom ESLint rules for textbox slice (P0 already covers)
//
// Single SoT: textbox payload, renderer, list-helper, edit-controller,
// lifecycle-controller, slice-local commands. No HTML overlay, no global
// commands, no cross-slice coupling. Extensions must amend the PRD first.

import type { SlicePlugin } from '../../core/object-registry.js';
import {
  type CapabilityDescriptor,
  TEXT_CONTENT_VIEW,
  RESIZE_EDIT,
  ANCHOR_TARGET_VIEW,
  BG_COLOR_VIEW,
  BG_COLOR_EDIT,
  BORDER_STYLE_VIEW,
  BORDER_STYLE_EDIT,
  FONT_SIZE_VIEW,
  FONT_SIZE_EDIT,
  FONT_STYLE_VIEW,
  FONT_STYLE_EDIT,
  STACK_PRESENCE_VIEW,
  UPDATABLE_PROPERTIES_EDIT,
  RICH_EDIT,
  LIST_TYPE_VIEW,
  LIST_TYPE_EDIT,
} from '../../core/capabilities/capability.interface.js';
import type { TextBox } from './textbox.interface.js';
import { createTextBoxDefaults } from './textbox-defaults.factory.js';
import { createNode, updateNode } from './textbox.renderer.js';
import { textToDocument } from './text-document-migration.js';
import type { TextDocument } from './text-document.type.js';

export const TEXTBOX_CAPABILITIES: readonly CapabilityDescriptor[] = [
  { capability: 'text-content', view: TEXT_CONTENT_VIEW },
  { capability: 'resize', edit: RESIZE_EDIT },
  { capability: 'anchor-target', view: ANCHOR_TARGET_VIEW },
  { capability: 'background-color', view: BG_COLOR_VIEW, edit: BG_COLOR_EDIT },
  { capability: 'border-style', view: BORDER_STYLE_VIEW, edit: BORDER_STYLE_EDIT },
  { capability: 'font-size', view: FONT_SIZE_VIEW, edit: FONT_SIZE_EDIT },
  { capability: 'font-style', view: FONT_STYLE_VIEW, edit: FONT_STYLE_EDIT },
  { capability: 'stack-presence', view: STACK_PRESENCE_VIEW },
  { capability: 'updatable-properties', edit: UPDATABLE_PROPERTIES_EDIT },
  { capability: 'rich-edit', edit: RICH_EDIT },
  { capability: 'list-type', view: LIST_TYPE_VIEW, edit: LIST_TYPE_EDIT },
];

/**
 * A document is considered "empty" if it contains a single empty paragraph
 * (or no content at all). This is the default shape produced by
 * `createTextBoxDefaults` for freshly placed textboxes; treating it as
 * "missing" lets the hydrate path re-migrate legacy payloads that were
 * persisted with `text` set and `document` defaulted to an empty paragraph.
 */
function isEmptyDocument(doc: TextDocument | undefined): boolean {
  if (!doc) return true;
  if (!doc.content || doc.content.length === 0) return true;
  if (doc.content.length === 1) {
    const first = doc.content[0];
    if (first?.type === 'paragraph' && (!first.content || first.content.length === 0)) {
      return true;
    }
  }
  return false;
}

/**
 * Migrate legacy persisted TextBox payloads into the canonical TipTap
 * document shape (PRD-RTE-TIPTAP-document-model §TR3 / §T2.5).
 *
 * - If `obj.document` is missing or an empty placeholder AND `obj.text` is
 *   non-empty, derive the document from `text` via `textToDocument` and clear
 *   the in-memory `text` field so the two payloads cannot drift.
 * - Already-migrated objects (non-empty document) pass through untouched.
 */
function hydrateTextBox(obj: TextBox): TextBox {
  const hasText = typeof obj.text === 'string' && obj.text.length > 0;
  if (hasText && isEmptyDocument(obj.document)) {
    const document = textToDocument(obj.text, obj.listType, {
      fontWeight: obj.fontWeight,
      textTransform: obj.textTransform,
      verticalScript: obj.verticalScript,
    });
    return { ...obj, document, text: '' };
  }
  return obj;
}

export const textboxSlice: SlicePlugin<TextBox> = {
  type: 'text',
  role: 'primary',
  capabilities: TEXTBOX_CAPABILITIES,
  display: {
    iconUrl: 'fa-font',
    label: 'Textbox',
  },
  createDefaults: createTextBoxDefaults,
  createNode,
  updateNode,
  isAnchorTarget: () => true,
  hydrate: hydrateTextBox,
};
